import request from "helper/request";

const facturaApi = {
  // Get all facturas with pagination
  getFacturas: (params = {}) => {
    return request.get("admin/facturas", { params });
  },

  // Get single factura by ID
  getFactura: (id) => {
    return request.get(`admin/facturas/${id}`);
  },

  // Create new factura
  createFactura: (data) => {
    return request.post("admin/facturas", data);
  },

  // Update existing factura
  updateFactura: (id, data) => {
    return request.put(`admin/facturas/${id}`, data);
  },

  // Delete factura
  deleteFactura: (id) => {
    return request.delete(`admin/facturas/${id}`);
  },

  // Get all clients for dropdown
  getClients: () => {
    return request.get("admin/clients", { params: { pageSize: 10000 } });
  },

  // Get only the clients that have invoices within a date range
  getClientsWithInvoices: (params = {}) => {
    return request.get("admin/facturas/clients-with-invoices", { params });
  },

  // Get factura logs
  getFacturaLogs: (facturaId) => {
    return request.get(`admin/facturas/${facturaId}/logs`);
  },

  // Add factura log
  addFacturaLog: (facturaId, note) => {
    return request.post(`admin/facturas/${facturaId}/logs`, { note });
  },

  // Get billing logs for a client
  getBillingLogs: (clientId, params = {}) => {
    return request.get(`admin/facturas/client/${clientId}/billing-logs`, { params });
  },

  // Add manual billing log for a client
  addManualBillingLog: (clientId, note) => {
    return request.post(`admin/facturas/client/${clientId}/billing-logs`, { note });
  },

  // Pay invoice via Stripe
  payInvoice: (facturaId) => {
    return request.post(`admin/facturas/${facturaId}/pay`);
  },

  // Pay partial amount via Stripe
  payPartialInvoice: (facturaId, amount, note) => {
    return request.post(`admin/facturas/${facturaId}/pay-partial`, { amount, note });
  },

  // Pay all invoices via Stripe (admin-side)
  payAllInvoices: (invoiceIds, summary) => {
    return request.post(`admin/facturas/pay-all`, { invoiceIds, summary });
  },

  // Export factura PDF
  exportFacturaPdf: (facturaId) => {
    return request.get(`admin/facturas/${facturaId}/export-pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" }
    });
  },

  // Add routes to existing factura
  addRoutesToFactura: (facturaId, routeIds, currentTotal = null) => {
    return request.post(`admin/facturas/${facturaId}/routes`, {
      routeIds,
      preserveExistingTotal: true,
      currentTotal
    });
  },

  // Create new factura with routes
  createFacturaWithRoutes: (clientId, routeIds) => {
    return request.post(`admin/facturas/create-with-routes`, { clientId, routeIds });
  },

  // Create new factura from selected routes (validates same client)
  createFacturaFromSelectedRoutes: (routeIds) => {
    return request.post(`admin/facturas/create-from-selected-routes`, { routeIds });
  },

  // Get unpaid facturas for a specific client
  getUnpaidFacturas: async (clientId) => {
    try {
      // Make two separate requests for unpaid and partial facturas
      const [unpaidResponse, partialResponse] = await Promise.all([
        request.get("admin/facturas", {
          params: {
            clientId,
            status: "unpaid",
            pageSize: 50
          }
        }),
        request.get("admin/facturas", {
          params: {
            clientId,
            status: "partial",
            pageSize: 50
          }
        })
      ]);

      // Combine the results
      const unpaidFacturas = unpaidResponse.data || [];
      const partialFacturas = partialResponse.data || [];
      const allFacturas = [...unpaidFacturas, ...partialFacturas];

      // Return in same format as original API
      return {
        data: allFacturas,
        total: allFacturas.length,
        page: 1,
        pageSize: 50
      };
    } catch (error) {
      console.error("Error fetching unpaid facturas:", error);
      throw error;
    }
  },

  // Email factura to client
  emailFactura: (facturaId, data) => {
    return request.post(`admin/facturas/${facturaId}/email`, data);
  },

  // Add charge to factura
  addChargeToFactura: (facturaId, chargeData, currentTotal = null) => {
    return request.post(`admin/facturas/${facturaId}/charges`, {
      ...chargeData,
      preserveExistingTotal: true,
      currentTotal
    });
  },

  // Add entitlement to factura
  addEntitlementToFactura: (facturaId, entitlementData, currentTotal = null) => {
    return request.post(`admin/facturas/${facturaId}/entitlements`, {
      ...entitlementData,
      preserveExistingTotal: true,
      currentTotal
    });
  },

  // Add file to factura
  addFileToFactura: (facturaId, formData) => {
    return request.post(`admin/facturas/${facturaId}/files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  },

  // Remove file from factura
  removeFileFromFactura: (facturaId, fileId) => {
    return request.delete(`admin/facturas/${facturaId}/files/${fileId}`);
  },

  // Get unassigned pool packages (packages without facturaId)
  getUnassignedPoolPackages: () => {
    return request.get("admin/pool-packages/unassigned");
  },

  // Get unassigned pool packages for specific client
  getUnassignedPoolPackagesForClient: (clientId) => {
    return request.get("admin/pool-packages/unassigned", {
      params: { clientId }
    });
  },

  // Add pool packages to existing factura
  addPoolPackagesToFactura: (facturaId, packageIds, currentTotal = null) => {
    return request.post(`admin/facturas/${facturaId}/pool-packages`, {
      packageIds,
      preserveExistingTotal: true,
      currentTotal
    });
  },

  // Create new factura with pool packages
  createFacturaWithPoolPackages: (clientId, packageIds) => {
    return request.post(`admin/facturas/create-with-pool-packages`, {
      clientId,
      packageIds
    });
  },

  // Get unassigned routes
  getUnassignedRoutes: () => {
    return request.get("admin/routes", {
      params: { facturaId: null, pageSize: 1000, includeStops: true }
    });
  },

  // Get unassigned routes for specific client
  getUnassignedRoutesForClient: (clientId) => {
    return request.get("admin/routes", {
      params: {
        clientId,
        facturaId: null,
        pageSize: 1000,
        includeStops: true,
        populate: "stops,totalStops"
      }
    });
  },

  // Get recent unassigned routes for specific client (limited to 50, ordered by pickup date desc)
  getRecentUnassignedRoutesForClient: (clientId) => {
    const pickupFilter = JSON.stringify({
      status: [],
      pickupDateRange: null,
      clientId: clientId,
      facturaId: null
    });

    return request.get("admin/routes", {
      params: {
        group: "flexio",
        page: 1,
        pageSize: 50,
        pickupFilter: pickupFilter,
        sortBy: "pickupDate",
        sortOrder: "desc"
      }
    });
  },

  // Get available entitlements for specific client
  getAvailableEntitlementsForClient: (clientId) => {
    return request.get("admin/billing/entitlements", {
      params: { clientId, excludeAssigned: true }
    });
  },

  // Get package types for lookup
  getPackageTypes: () => {
    return request.get("admin/package-types");
  },

  // Remove route from factura
  removeRouteFromFactura: (facturaId, routeId, currentTotal = null) => {
    return request.delete(`admin/facturas/${facturaId}/routes/${routeId}`, {
      data: {
        preserveExistingTotal: true,
        currentTotal
      }
    });
  },

  // Remove pool package from factura
  removePoolPackageFromFactura: (facturaId, packageId, currentTotal = null) => {
    return request.delete(`admin/facturas/${facturaId}/pool-packages/${packageId}`, {
      data: {
        preserveExistingTotal: true,
        currentTotal
      }
    });
  },

  // Remove entitlement from factura
  removeEntitlementFromFactura: (facturaId, entitlementId, currentTotal = null) => {
    return request.delete(`admin/facturas/${facturaId}/entitlements/${entitlementId}`, {
      data: {
        preserveExistingTotal: true,
        currentTotal
      }
    });
  },

  // Remove charge from factura
  removeChargeFromFactura: (facturaId, chargeId, currentTotal = null) => {
    return request.delete(`admin/facturas/${facturaId}/charges/${chargeId}`, {
      data: {
        preserveExistingTotal: true,
        currentTotal
      }
    });
  },

  // Add tier to factura
  addTierToFactura: (facturaId, tierData, currentTotal = null) => {
    return request.post(`admin/facturas/${facturaId}/tiers`, {
      ...tierData,
      preserveExistingTotal: true,
      currentTotal
    });
  },

  // Remove tier from factura
  removeTierFromFactura: (facturaId, tierId, currentTotal = null) => {
    return request.delete(`admin/facturas/${facturaId}/tiers/${tierId}`, {
      data: {
        preserveExistingTotal: true,
        currentTotal
      }
    });
  }
};

export default facturaApi;
